import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Send, RefreshCw, User, Clock, Tag, MessageSquare,
  CheckCircle, XCircle, AlertCircle, Edit
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TicketDetail = () => {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [hasAccess, setHasAccess] = useState(false);

  // Check permission on load
  useEffect(() => {
    const checkAccess = () => {
      try {
        const token = localStorage.getItem('monitoringAdminToken');
        if (!token) {
          navigate('/monitoring-dashboard/login');
          return;
        }

        // Decode token to check permissions
        const payload = JSON.parse(window.atob(token.split('.')[1]));
        const isAdmin = payload.user_type === 'admin';
        const canAccessTickets = payload.can_access_tickets === true;

        if (!isAdmin && !canAccessTickets) {
          navigate('/monitoring-dashboard');
          return;
        }

        setHasAccess(true);
      } catch (error) {
        console.error('Error checking access:', error);
        navigate('/monitoring-dashboard/login');
      }
    };

    checkAccess();
  }, [navigate]);

  useEffect(() => {
    if (hasAccess) {
      fetchTicketDetails();
      fetchReplies();
    }
  }, [hasAccess, ticketId]);

  const fetchTicketDetails = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/smart-tickets/${ticketId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTicket(data);
        setNewStatus(data.status);
      } else {
        toast.error('Ticket not found');
        navigate('/monitoring-dashboard/tickets');
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/smart-tickets/${ticketId}/replies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReplies(data);
      }
    } catch (error) {
      console.error('Error fetching replies:', error);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/smart-tickets/${ticketId}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: replyMessage,
          is_internal: false
        })
      });

      if (response.ok) {
        toast.success('Reply sent!');
        setReplyMessage('');
        fetchReplies();
        fetchTicketDetails();
      } else {
        toast.error('Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async () => {
    if (newStatus === ticket.status) return;

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/smart-tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Status updated!');
        fetchTicketDetails();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'text-red-600 bg-red-100';
      case 'High': return 'text-orange-600 bg-orange-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'text-blue-600 bg-blue-100';
      case 'In Progress': return 'text-purple-600 bg-purple-100';
      case 'Resolved': return 'text-green-600 bg-green-100';
      case 'Closed': return 'text-gray-600 bg-gray-100';
      case 'On Hold': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Ticket not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/monitoring-dashboard/tickets')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Tickets
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {ticket.ticket_id}
              </h1>
              <p className="text-sm text-gray-600">{ticket.subject}</p>
            </div>
          </div>
          <Button
            onClick={() => {
              fetchTicketDetails();
              fetchReplies();
            }}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
            </CardContent>
          </Card>

          {/* Conversation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Conversation ({replies.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-6">
                {replies.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No replies yet</p>
                ) : (
                  replies.map((reply) => (
                    <div
                      key={reply.reply_id}
                      className={`p-4 rounded-lg ${
                        reply.author_role === 'admin'
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : 'bg-gray-50 border-l-4 border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-sm">{reply.author_name}</span>
                          <span className="text-xs text-gray-500">
                            ({reply.author_role})
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDate(reply.created_at)}
                        </span>
                      </div>
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">
                        {reply.message}
                      </p>
                      {reply.is_internal && (
                        <span className="inline-block mt-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                          Internal Note
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Reply Input */}
              <div className="border-t pt-4">
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply..."
                  className="w-full border rounded-lg p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    onClick={handleSendReply}
                    disabled={sending || !replyMessage.trim()}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                  >
                    {sending ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Send Reply
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Ticket Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ticket Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Customer</label>
                <p className="font-medium">{ticket.customer_name}</p>
                {ticket.customer_ip && (
                  <p className="text-sm text-gray-500 font-mono">{ticket.customer_ip}</p>
                )}
              </div>

              <div>
                <label className="text-sm text-gray-600">Priority</label>
                <div className="mt-1">
                  <span className={`px-3 py-1 rounded text-sm font-semibold ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority}
                  </span>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 block mb-2">Status</label>
                <div className="flex items-center gap-2">
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="border rounded px-3 py-2 text-sm flex-1"
                  >
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </select>
                  {newStatus !== ticket.status && (
                    <Button
                      size="sm"
                      onClick={handleStatusChange}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600">Category</label>
                <p className="font-medium">{ticket.category}</p>
              </div>

              <div>
                <label className="text-sm text-gray-600">Created</label>
                <p className="text-sm">{new Date(ticket.created_at).toLocaleString()}</p>
              </div>

              {ticket.first_response_at && (
                <div>
                  <label className="text-sm text-gray-600">First Response</label>
                  <p className="text-sm">{new Date(ticket.first_response_at).toLocaleString()}</p>
                </div>
              )}

              {ticket.resolved_at && (
                <div>
                  <label className="text-sm text-gray-600">Resolved</label>
                  <p className="text-sm">{new Date(ticket.resolved_at).toLocaleString()}</p>
                </div>
              )}

              {ticket.is_overdue && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-semibold">OVERDUE</span>
                  </div>
                  <p className="text-sm text-red-600 mt-1">
                    This ticket has exceeded the SLA response time
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
